import { useContext, useEffect } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import { Select } from 'components/Select';
import { Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { Tenant } from 'pages/QuoteView/LoadBuilding/McleodSectionForms/constants';
import {
  getCommodityOptions,
  getMcleodIntegrationFromList,
  getOrderTypeOptions,
} from 'pages/QuoteView/LoadBuilding/McleodSectionForms/helpers';
import {
  EQUIPMENT_PLACEHOLDER,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { NormalizedLoad, Specifications, Unit } from 'types/Load';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { devDisableRequiredFields } from 'utils/loadInfoAndBuilding';
import { cn } from 'utils/shadcn';

import { LoadBuildingTextInput } from '../components/LoadBuildingTextInput';

export enum RateType {
  Flat = 'Flat',
  Distance = 'Distance',
  CWT = 'CWT',
  Tons = 'Tons',
}

export function SpecificationsForm({
  formMethods,
  transportTypeOptions,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  transportTypeOptions: {
    value: string;
    label: string;
  }[];
}) {
  const {
    control,
    formState: { errors },
    watch,
  } = formMethods;

  const { tmsIntegrations } = useServiceFeatures();
  const tms = getMcleodIntegrationFromList(tmsIntegrations);
  const shouldShowOrderTypeInput = tms
    ? !tms.tenant.includes(Tenant.Fetch) // Show order type input for non-Fetch tenants
    : true;

  // BE uses TL/LTL; form stores key, displays label
  const modeOptions = [
    { value: 'TL', label: 'Truckload' },
    { value: 'LTL', label: 'LTL' },
  ];

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  const specs: Specifications = {
    totalWeight: { unit: Unit.Pounds },
    totalDistance: { unit: Unit.Miles },
    totalPieces: { unit: null, val: 0 },
  } as Specifications;

  const isWeightSupported = true;
  const isPiecesSupported = true;

  const isDistanceSupported = false;
  const isPalletSupported = tms ? tms.tenant.includes('fcfm') : false;

  const watchedCustomerRateType = watch('rateData.customerRateType');
  const arePalletsRequired = watch('specifications.palletsRequired');
  const planningComment = watch('specifications.planningComment');

  const commodityOptions = getCommodityOptions(tms);

  const palletOptions = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  useEffect(() => {
    if (!arePalletsRequired) {
      formMethods.setValue('specifications.totalInPalletCount', null);
    }
    // formMethods is stable enough; including it retriggers on every form identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arePalletsRequired]);

  return (
    <Grid cols='2' gap='sm' className='mx-0 w-full'>
      {/* Only Truckloads supported rn, not LTL */}
      <div
        className={cn(shouldShowOrderTypeInput ? 'col-span-1' : 'col-span-2')}
      >
        <Label hideAIHint={true} name={'mode'} required={true}>
          Mode
        </Label>
        <Controller
          name='mode'
          control={control}
          rules={{ required: devDisableRequiredFields ? false : 'Required' }}
          render={({ field }) => (
            <div className='text-neutral-500'>
              <Select
                showSearch
                disabled={true} // Only Truckloads supported rn, not LTL
                className='h-8'
                placeholder={'Choose'}
                optionFilterProp='children'
                onChange={field.onChange}
                value={field.value || 'TL'}
                options={modeOptions}
              />
            </div>
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'mode'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      {shouldShowOrderTypeInput && (
        <div className='col-span-1'>
          <Label name={'specifications.orderType'}>Order Type</Label>
          <Controller
            name='specifications.orderType'
            control={control}
            rules={{ required: false }}
            render={({ field }) => (
              <div className=' text-neutral-500'>
                <Select
                  showSearch
                  className='h-8'
                  placeholder={'Choose'}
                  optionFilterProp='children'
                  onChange={field.onChange}
                  value={field.value}
                  options={getOrderTypeOptions(tms)?.map((type) => ({
                    value: type.value,
                    label: type.label,
                  }))}
                />
              </div>
            )}
          />
          <ErrorMessage
            errors={errors}
            name={'specifications.orderType'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
      )}

      <div className='col-span-2'>
        <Label
          name={'specifications.transportType'}
          // We always default this value so don't show AI hint unless there's a suggestion
          hideAIHint={
            !clickedSuggestion ||
            clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
          }
          required={true}
        >
          Transport Type
        </Label>
        <Controller
          name='specifications.transportType'
          control={control}
          rules={{
            validate: devDisableRequiredFields
              ? undefined
              : validateEquipmentRequired,
          }}
          render={({ field }) => (
            <Select
              showSearch
              className='h-8'
              placeholder={EQUIPMENT_PLACEHOLDER}
              optionFilterProp='children'
              onChange={field.onChange}
              value={field.value}
              options={transportTypeOptions?.map((type) => ({
                value: type.value,
                label: type.label,
              }))}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.transportType'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      <div className='col-span-2'>
        <Label
          name={'specifications.commodities'}
          // We always default this value so don't show AI hint unless there's a suggestion
          hideAIHint={
            !clickedSuggestion ||
            clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
          }
        >
          Commodity
        </Label>
        <Controller
          name='specifications.commodities'
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <Select
              showSearch
              className='h-8'
              placeholder={'Choose'}
              optionFilterProp='children'
              onChange={field.onChange}
              value={field.value}
              options={commodityOptions?.map((type) => ({
                value: type.code,
                label: type.description,
              }))}
            />
          )}
        />
        <ErrorMessage
          errors={errors}
          name={'specifications.commodities'}
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      {isPiecesSupported && (
        <div
          className={
            isPiecesSupported && !isWeightSupported
              ? 'col-span-2'
              : 'col-span-1'
          }
        >
          <LoadBuildingTextInput
            name='specifications.totalPieces.val'
            label={`Pieces${specs.totalPieces?.unit ? ' (' + specs.totalPieces?.unit + ')' : ''}`}
            inputType='number'
            options={{ valueAsNumber: true }}
          />
        </div>
      )}
      {isWeightSupported && (
        <div
          className={
            isWeightSupported && !isPiecesSupported
              ? 'col-span-2'
              : 'col-span-1'
          }
        >
          <LoadBuildingTextInput
            name='specifications.totalWeight.val'
            label={`Weight${specs.totalWeight?.unit ? ' (' + specs.totalWeight?.unit + ')' : ''}`}
            inputType='number'
            options={{
              valueAsNumber: true,
              required:
                watchedCustomerRateType === RateType.CWT ||
                watchedCustomerRateType === RateType.Tons
                  ? 'Required for weight-based rates'
                  : undefined,
            }}
            required={
              watchedCustomerRateType === RateType.CWT ||
              watchedCustomerRateType === RateType.Tons
            }
          />
        </div>
      )}
      <div className='col-span-1'>
        <Label
          name={'specifications.palletsRequired'}
          // We always default this value so don't show AI hint unless there's a suggestion
          hideAIHint={
            !clickedSuggestion ||
            clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
          }
        >
          Pallets Required
        </Label>
        <Controller
          name='specifications.palletsRequired'
          control={control}
          rules={{ required: false }}
          render={({ field }) => (
            <Select
              className='h-8'
              placeholder={'Choose'}
              optionFilterProp='children'
              onChange={(value) => {
                let nextValue: boolean | typeof value = value;
                if (value === 'true') {
                  nextValue = true;
                } else if (value === 'false') {
                  nextValue = false;
                }
                field.onChange(nextValue);
              }}
              value={
                typeof field.value === 'boolean'
                  ? String(field.value)
                  : field.value
              }
              options={palletOptions}
            />
          )}
        />
      </div>
      {isPalletSupported && arePalletsRequired && (
        <div className={'col-span-1'}>
          <LoadBuildingTextInput
            name='specifications.totalInPalletCount'
            label='Pallet Count'
            inputType='number'
            options={{
              valueAsNumber: true,
            }}
          />
        </div>
      )}
      <div className='col-span-2'>
        <LoadBuildingTextInput
          name='specifications.planningComment'
          label='Planning Comment'
          inputType='text'
          defaultValue={planningComment ?? ''}
        />
      </div>
      {isDistanceSupported && (
        <div
          className={
            isDistanceSupported && !isWeightSupported
              ? 'col-span-2'
              : 'col-span-1'
          }
        >
          <LoadBuildingTextInput
            name='specifications.totalDistance.val'
            label={`Distance${specs.totalDistance?.unit ? ' (' + specs.totalDistance?.unit + ')' : ''}`}
            inputType='number'
            options={{ valueAsNumber: true }}
          />
        </div>
      )}
    </Grid>
  );
}
